import Image from "@components/common/CloudinaryImage";
import { CartContext } from "@components/contexts/CartContext";
import addToBasketSfx from "@static/sounds/addProduct.mp3";
import { Link } from "gatsby";
import firebase from "gatsby-plugin-firebase";
import React, { useContext, useEffect, useState } from "react";
import {
    FiBookOpen,
    FiPackage,
    FiRefreshCw,
    FiSmartphone,
} from "react-icons/fi";
import {
    MdAddShoppingCart,
    MdHourglassEmpty,
    MdInfoOutline,
} from "react-icons/md";
import { toast } from "react-toastify";
import useSound from "use-sound";

import "@styles/Product.css";

import { trackFbAddToCart } from "@base/utils/fb";
import { BiHide, BiShow } from "react-icons/bi";

const Products = ({ items }) => {
    const [play] = useSound(addToBasketSfx, { volume: 0.05 });
    const [products, setProducts] = useState(items);
    const [hideNoStock, setHideNoStock] = useState(false);
    const [textFilter, setTextFilter] = useState("");

    useEffect(() => {
        setProducts(items);
    }, [items]);

    useEffect(() => {
        if (hideNoStock) {
            if (textFilter === "") {
                setProducts(items.filter((item) => item.stock && !item.parent));
            } else {
                setProducts(
                    items
                        .filter((item) => item.stock && !item.parent)
                        .filter(
                            (item) =>
                                item.title
                                    .toLowerCase()
                                    .normalize("NFD")
                                    .replace(/\p{Diacritic}/gu, "")
                                    .includes(
                                        textFilter
                                            .toLowerCase()
                                            .normalize("NFD")
                                            .replace(/\p{Diacritic}/gu, ""),
                                    ) ||
                                item.stock_message?.toLowerCase().includes(
                                    textFilter
                                        .toLowerCase()
                                        .normalize("NFD")
                                        .replace(/\p{Diacritic}/gu, ""),
                                ) ||
                                `${item.id}`.includes(textFilter),
                        ),
                );
            }
        } else {
            if (textFilter === "") {
                setProducts(items.filter((item) => !item.parent));
            } else {
                setProducts(
                    items.filter(
                        (item) =>
                            item.title
                                .toLowerCase()
                                .normalize("NFD")
                                .replace(/\p{Diacritic}/gu, "")
                                .includes(
                                    textFilter
                                        .toLowerCase()
                                        .normalize("NFD")
                                        .replace(/\p{Diacritic}/gu, ""),
                                ) ||
                            item.stock_message?.toLowerCase().includes(
                                textFilter
                                    .toLowerCase()
                                    .normalize("NFD")
                                    .replace(/\p{Diacritic}/gu, ""),
                            ) ||
                            `${item.id}`.includes(textFilter),
                    ),
                );
            }
        }
    }, [hideNoStock, items, textFilter]);

    return (
        <>
            {items && items.length > 0 ? (
                <>
                    <div className="flex flex-col items-center gap-5 md:flex-row md:justify-between">
                        <div className="w-full">
                            <input
                                type="text"
                                placeholder="Filtrar por texto"
                                className="w-full rounded border border-gray-300 px-3 py-2 text-xs text-gray-700"
                                onChange={(e) =>
                                    setTextFilter(
                                        e.target.value.toLocaleLowerCase(),
                                    )
                                }
                                value={textFilter}
                            />
                        </div>

                        {items.some((x) => !x.stock) && (
                            <div className="flex w-full items-center md:max-w-md">
                                <button
                                    onClick={() => setHideNoStock(true)}
                                    title="Ocultar sin stock"
                                    className={`flex h-full w-1/2 items-center justify-center space-x-2 rounded-l border-r border-white px-3 py-2 text-xs text-white shadow duration-300 ${
                                        hideNoStock
                                            ? "bg-ccyan-dark shadow-xl"
                                            : "shadown-none bg-ccyan"
                                    }`}
                                >
                                    <BiHide className="h-4 w-4" />
                                    <span className="truncate-1-lines">
                                        Ocultar sin stock
                                    </span>
                                </button>

                                <button
                                    onClick={() => setHideNoStock(false)}
                                    title="Mostrar todos"
                                    className={`flex w-1/2 items-center justify-center space-x-2 rounded-r px-3 py-2 text-xs text-white shadow duration-300 ${
                                        !hideNoStock
                                            ? "bg-ccyan-dark shadow-xl"
                                            : "bg-ccyan shadow-none"
                                    }`}
                                >
                                    <BiShow className="h-4 w-4" />
                                    <span>Mostrar todos</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="products-list mt-5 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
                        {products.map((p) => {
                            return (
                                <Product
                                    items={items}
                                    key={p.id}
                                    item={p}
                                    play={play}
                                />
                            );
                        })}
                    </div>
                </>
            ) : (
                <div className="mt-5 text-center text-sm text-gray-600">
                    No hay productos disponibles.
                </div>
            )}
        </>
    );
};

const SubProducts = ({ item, items, play }) => {
    // Definimos estados
    const { addProduct, cartItems } = useContext(CartContext);
    const [selectedItem, setSelectedItem] = useState(null);

    // Obtengo los childs mezclando el id con la lista de items
    const childs = items.filter(
        (i) => item.childs.includes(i.id) && i.stock && i.enabled,
    );

    // Busca el producto por defecto en el array de childs
    const defaultItem = childs.find((p) => p.default_in_parent) || childs[0];

    // Inicializa el estado seleccionado con el producto por defecto
    useEffect(() => {
        setSelectedItem(defaultItem);
    }, [defaultItem]);

    // Estado interno para controlar el valor del select
    const [selectedSku, setSelectedSku] = useState(defaultItem.sku);

    const handleSelectChange = (event) => {
        setSelectedSku(event.target.value);
    };

    const handleSelectBlur = () => {
        const selectedProduct = childs.find((p) => p.sku === selectedSku);
        setSelectedItem(selectedProduct);
    };

    // Si no hay childs no mostramos nada
    if (!childs) return null;

    return (
        <div className="mx-5 flex items-center justify-center gap-3">
            <select
                className="flex-shrink overflow-hidden rounded-md border bg-white px-3 py-3 text-sm"
                value={selectedSku}
                onChange={handleSelectChange}
                onBlur={handleSelectBlur}
            >
                {childs?.map((p) => {
                    const exist = cartItems.some((i) => i.id === p.id);

                    return (
                        <option
                            key={p.id}
                            value={p.sku}
                            className={`bg-white ${exist && "font-semibold text-cpurple"} truncate-1-lines`}
                        >
                            {p.short} - {p.price.toFixed(2)} €
                        </option>
                    );
                })}
            </select>

            <button
                title="Añadir producto al carrito"
                className="small-product-button button-buy"
                disabled={!item.stock}
                onClick={() => {
                    // Añadimos el producto al carrito
                    addProduct(selectedItem);

                    // Reproducimos el sonido
                    play();

                    // En caso de añadir al carrito
                    (async () => {
                        await trackFbAddToCart({
                            currency: "EUR",
                            value: selectedItem.price,
                        });
                    })();

                    firebase.analytics().logEvent("add_to_cart", {
                        item_id: selectedItem.sku,
                        item_name: selectedItem.title,
                        item_category: selectedItem.type,
                        price: selectedItem.price,
                        currency: "EUR",
                        quantity: 1,
                    });

                    // Mostramos mensaje por pantalla
                    toast.info("Producto añadido al carro de la compra.", {
                        position: "bottom-center",
                        closeOnClick: true,
                        pauseOnHover: true,
                        autoClose: 5000,
                    });
                }}
            >
                <MdAddShoppingCart
                    className="inline h-5 w-5"
                    style={{ verticalAlign: "middle" }}
                />
            </button>
        </div>
    );
};

const Product = ({ item, play, items }) => {
    const { addProduct } = useContext(CartContext);
    const images = item.images || [];
    const defaultImage = images.length > 0 ? images[0] : "products/common/0";

    return (
        <>
            <article className="product product-item bg-white">
                <div className="product-container">
                    <Image alt={item.name} filename={defaultImage} />
                    <div className="border-t border-gray-200 bg-white p-5">
                        <h3 title={item.title} className="h3">
                            {item.title}
                        </h3>
                        <div className="mt-5 text-center text-gray-600">
                            {/** Missatge de descompte */}
                            {item.stock && item.offer && item.offer_text ? (
                                <div className="mb-5 rounded bg-red-600 px-5 py-2 text-xs uppercase text-white shadow">
                                    {item.offer_text}
                                </div>
                            ) : null}

                            {item.products?.length > 0 && (
                                <span className="text-sm text-red-500">
                                    Según modalidad
                                    <br />
                                    seleccionada a partir de{" "}
                                </span>
                            )}

                            {item.special_buy ? (
                                <span className="font-semibold text-green-500">
                                    {item.price} €
                                </span>
                            ) : item.stock ? (
                                item.offer ? (
                                    <span className="font-semibold text-green-500">
                                        {item.price} €
                                    </span>
                                ) : (
                                    <>
                                        {!item.split_payment && (
                                            <span>{item.price} €</span>
                                        )}
                                    </>
                                )
                            ) : (
                                <span className="text-yellow-500">
                                    {item.stock_message}
                                </span>
                            )}

                            {item.stock ? (
                                item.offer ? (
                                    <span className="ml-2 text-red-500 line-through">
                                        {item.offer}
                                    </span>
                                ) : null
                            ) : null}

                            {item.stock && item.split_payment && (
                                <span className="font-medium text-green-600">
                                    {" "}
                                    {item.split_payment}
                                </span>
                            )}

                            {/** Mostramos aviso de poco stock */}
                            {item?.stock && item?.lowStock && (
                                <div className="mt-2 flex items-center justify-center space-x-2 text-sm font-semibold text-red-500">
                                    <MdHourglassEmpty className="h-5 w-5" />
                                    <>
                                        {item.low_stock_message ||
                                            "¡Últimas unidades disponibles!"}
                                    </>
                                </div>
                            )}

                            {/** Mostramos aviso de poco stock */}
                            {/*{item.stock &&
                            (item?.evolmind?.limit > 0 || item?.limit > 0) &&
                            (item?.evolmind?.enrolled || 1) /
                                (item.evolmind?.limit || 1) >
                                0.65 ? (
                                <div className="text-red-500 text-sm font-semibold flex items-center space-x-2 justify-center mt-2">
                                    <MdHourglassEmpty className="w-5 h-5" />
                                    {item.low_stock_message ||
                                        "¡Últimas plazas disponibles!"}
                                </div>
                            ) : (
                                <></>
                            )}*/}

                            {item.stock &&
                            item.recurrent &&
                            item.recurrent === "monthly" ? (
                                <div className="inline-block">
                                    <span className="ml-2 text-sm text-orange-700">
                                        [mensual]
                                    </span>
                                    <FiRefreshCw className="ml-2 inline" />
                                </div>
                            ) : null}

                            {item.stock && item.can_be_sent ? (
                                <span className="ml-2 text-xs text-ccyan">
                                    +{" "}
                                    <FiPackage
                                        title="+ Envío"
                                        className="inline h-5 w-5"
                                    />
                                </span>
                            ) : (
                                ""
                            )}

                            {item.stock && item.app?.modules?.length > 0 && (
                                <span className="ml-2 text-xs text-ccyan">
                                    ={" "}
                                    <FiBookOpen
                                        title="Incluye libro físico"
                                        className="ml-2 inline h-5 w-5"
                                    />
                                    {" + "}
                                    <FiSmartphone
                                        title="Incluye acceso desde la app"
                                        className="inline h-5 w-5"
                                    />
                                </span>
                            )}
                        </div>
                    </div>

                    {/** Mostramos las subopciones de producto */}
                    {item?.childs?.length > 0 && (
                        <SubProducts item={item} play={play} items={items} />
                    )}

                    <div
                        className="absolute bottom-0 right-0 z-[1] w-full pb-3 pr-0 text-right uppercase text-gray-400"
                        style={{ fontSize: "11px" }}
                    >
                        <div className="flex items-center justify-between px-5">
                            <div></div>
                            <div>Ref: {item.id}</div>
                        </div>
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-2">
                    {item.stock && !item.special_buy && !item?.childs && (
                        <button
                            title="Comprar producto"
                            className={`product-button button-buy flex justify-evenly`}
                            disabled={!item.stock}
                            onClick={() => {
                                // clearCart();
                                addProduct(item);
                                play();

                                // En caso de añadir al carrito
                                (async () => {
                                    await trackFbAddToCart({
                                        currency: "EUR",
                                        value: item.price,
                                    });
                                })();

                                firebase.analytics().logEvent("add_to_cart", {
                                    item_id: item.sku,
                                    item_name: item.title,
                                    item_category: item.type,
                                    price: item.price,
                                    currency: "EUR",
                                    quantity: 1,
                                });

                                toast.info(
                                    "Producto añadido al carro de la compra.",
                                    {
                                        position: "bottom-center",
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        autoClose: 5000,
                                    },
                                );
                            }}
                        >
                            <MdAddShoppingCart
                                className="inline h-6 w-6"
                                style={{ verticalAlign: "middle" }}
                            />
                            Comprar producto
                        </button>
                    )}

                    {item.stock && item.special_buy && (
                        <a
                            href={item.special_buy}
                            className={`product-button button-buy flex justify-evenly`}
                        >
                            <MdAddShoppingCart
                                className="inline h-6 w-6"
                                style={{ verticalAlign: "middle" }}
                            />
                            COMPRAR PRODUCTO/S
                        </a>
                    )}

                    {item.link && item.more_info && (
                        <Link
                            title="Más información"
                            to={item.link}
                            className="product-button flex justify-evenly"
                        >
                            <MdInfoOutline
                                className="inline h-6 w-6"
                                style={{ verticalAlign: "middle" }}
                            />
                            Más información
                        </Link>
                    )}
                </div>
            </article>
        </>
    );
};

export default Products;
